import * as dayjs from 'dayjs';
import { Component, ReactNode } from 'react';
import CurrencyInput from 'react-currency-input-field';
import DatePicker from 'react-date-picker';
import { NavLink, useNavigate, useParams } from 'react-router';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { toast } from 'react-toastify';
import { IdNameDto } from 'src/shared/api-result.dto';
import { AdminApi } from '../admin-api';
import Header from './Header';
import LoadingButton from './LoadingButton';
import PageLoading from './PageLoading';
import Sidebar from './Sidebar';

import 'react-calendar/dist/Calendar.css';
import 'react-date-picker/dist/DatePicker.css';
import { CustomEventPerson } from './CustomCalendar';

const customParseFormat = require('dayjs/plugin/customParseFormat');
dayjs.extend(customParseFormat);

export default function ExpenseEdit(props: any): ReactNode {
  return (
    <ExpenseEditComp navigate={useNavigate()} params={useParams()} {...props} />
  );
}

class ExpenseEditComp extends Component {
  constructor(
    readonly props: Readonly<{
      children?: ReactNode;
      navigate?: any;
      currentUser?: any;
      params?: any;
    }>,
  ) {
    super(props);
    this.state = {
      isLoading: true,
      isSubmitting: false,
      data: null,
      strValue: '',
      strDate: '',
      strInstallments: '',
      categories: [],
      users: [],
    };
  }

  state: Readonly<{
    isLoading: boolean;
    isSubmitting: boolean;
    categories: IdNameDto[];
    strValue: string;
    strDate: string;
    users: CustomEventPerson[];
    strInstallments: string;
    data: {
      description: string;
      category: string;
      date: string;
      value: number;
      id: number;
      isDone: boolean;
      hasInstallments: boolean;
      personId?: number;
      numberOfInstallments?: number;
    } | null;
  }>;

  async componentDidMount(): Promise<void> {
    const _id = this.props.params.id;

    const _allCat = await AdminApi.get(`/categories`);
    let _allUsers: CustomEventPerson[] = [];

    if (this.props.currentUser.role === 'admin') {
      _allUsers = await AdminApi.get(`/users`);
    }

    this.setState({
      categories: _allCat.map((item: IdNameDto) => ({
        value: item.id,
        label: item.name,
      })),
      users: _allUsers,
    });

    if (_id) {
      const _data = await AdminApi.get(`/expenses/${_id}`);

      this.setState({
        isLoading: false,
        data: _data,
        strValue: _data ? _data.value.toFixed(2).replace('.', ',') : '',
        strDate: _data ? dayjs(_data.date).toISOString() : '',
        strInstallments: _data?.numberOfInstallments
          ? _data.numberOfInstallments.toString()
          : '',
      });
    } else {
      this.setState({
        isLoading: false,
        data: {
          description: '',
          category: '',
          date: '',
          value: 0,
          isDone: false,
          personId: null,
          numberOfInstallments: 0,
        },
      });
    }
  }

  async submit() {
    let _result = null;

    this.setState({ isSubmiting: true });

    if (this.state.data) {
      this.state.data.date = this.state.strDate;
    }

    const _numberOfInstallments = parseInt(this.state.strInstallments || '0');

    if (this.state.data?.id) {
      _result = await AdminApi.put(`/expenses/${this.state.data.id}`, {
        description: this.state.data?.description,
        category: this.state.data?.category,
        date: this.state.data?.date,
        value: this.state.data?.value,
        isDone: this.state.data?.isDone,
        personId: this.state.data?.personId,
        numberOfInstallments: _numberOfInstallments,
      });
    } else {
      if (this.state.data) {
        this.state.data.numberOfInstallments = _numberOfInstallments;
      }

      _result = await AdminApi.post('/expenses', this.state.data);
    }

    this.setState({ isSubmiting: false });

    if (_result) {
      toast.success('Despesa salva com sucesso!');

      setTimeout(() => {
        this.props.navigate('/expenses');
      }, 200);
    }
  }

  canSubmit() {
    return (
      this.state.data?.description?.length &&
      this.state.strDate &&
      this.state.strValue
    );
  }

  render(): ReactNode {
    return this.state.isLoading ? (
      <PageLoading />
    ) : (
      <div>
        <Header
          navigate={this.props.navigate}
          currentUser={this.props.currentUser}
        ></Header>
        <div className="container-fluid">
          <div className="row">
            <Sidebar
              navigate={this.props.navigate}
              currentUser={this.props.currentUser}
            ></Sidebar>
            <main className="ms-sm-auto large-calendar px-md-4">
              <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
                <h1 className="h2">
                  {this.state.data?.id
                    ? `Editar ${this.state.data?.description}`
                    : 'Adicionar despesa'}
                </h1>
              </div>
              <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/expenses">Lista de despesas</NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Edição de despesa
                    </li>
                  </ol>
                </nav>
              </div>
              <div className="card">
                <div className="card-body">
                  <form>
                    {this.props.currentUser.role === 'admin' && (
                      <div className="mb-3">
                        <label className="form-label">Pessoa</label>
                        <Select
                          className="basic-single"
                          classNamePrefix="select"
                          isClearable={true}
                          name="personId"
                          placeholder="Despesa do Plurie"
                          options={this.state.users.map((user) => ({
                            value: user.id,
                            label: user.name,
                          }))}
                          value={
                            this.state.data?.personId
                              ? {
                                  value: this.state.data.personId,
                                  label: this.state.users.find(
                                    (u) => u.id === this.state.data?.personId,
                                  )?.name,
                                }
                              : null
                          }
                          onChange={(e: any) =>
                            this.setState({
                              data: {
                                ...this.state.data,
                                personId: e ? e.value : null,
                              },
                            })
                          }
                        />
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label">Categoria</label>
                      <CreatableSelect
                        options={this.state.categories}
                        value={
                          this.state.data?.category
                            ? this.state.data.category
                                .split(',')
                                .map((item: string) => ({
                                  value: item,
                                  label: item,
                                }))
                            : []
                        }
                        isClearable
                        isMulti
                        onChange={(e: any) =>
                          this.setState({
                            data: {
                              ...this.state.data,
                              category: e
                                .map((item: any) => item.value)
                                .join(','),
                            },
                          })
                        }
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Descrição</label>
                      <input
                        type="email"
                        className="form-control"
                        onChange={(e) =>
                          this.setState({
                            data: {
                              ...this.state.data,
                              description: e.target.value,
                            },
                          })
                        }
                        value={this.state.data?.description}
                        aria-describedby="descriptionHelp"
                      />
                      <div id="emailHelp" className="form-text">
                        Será identificado no relatório de despesas.
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Data</label>
                      <DatePicker
                        className="form-control"
                        format="dd/MM/yyyy"
                        clearIcon={null}
                        onChange={(d) => {
                          this.setState({
                            strDate: ((d ?? new Date()) as Date).toISOString(),
                          });
                        }}
                        value={this.state.strDate}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Valor</label>
                      <CurrencyInput
                        className="form-control"
                        allowNegativeValue={false}
                        value={this.state.strValue}
                        decimalSeparator=","
                        groupSeparator="."
                        decimalsLimit={2}
                        min={1}
                        onValueChange={(value, name, values) =>
                          this.setState({
                            strValue: value || '',
                            data: { ...this.state.data, value: values?.float },
                          })
                        }
                      />

                      <div id="valueHelp" className="form-text">
                        Informe o valor de cada parcela. Se não tiver parcelas
                        informe o valor total da despesa.
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          disabled={!!this.state.data?.id}
                          id="flexSwitchCheckDefault"
                          checked={this.state.data?.hasInstallments}
                          onChange={(e) =>
                            this.setState({
                              data: {
                                ...this.state.data,
                                hasInstallments: e.target.checked,
                              },
                            })
                          }
                          value={
                            this.state.data?.hasInstallments ? 'true' : 'false'
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="flexSwitchCheckDefault"
                        >
                          Esta despesa possui parcelas?
                        </label>
                      </div>
                    </div>

                    {this.state.data?.hasInstallments && (
                      <div className="mb-3">
                        <label className="form-label">Número de parcelas</label>
                        <input
                          disabled={!!this.state.data?.id}
                          type="number"
                          className="form-control"
                          onChange={(e) =>
                            this.setState({
                              strInstallments: e.target.value,
                            })
                          }
                          value={this.state.strInstallments || ''}
                          aria-describedby="descriptionHelp"
                        />
                        <div id="installmentHelp" className="form-text">
                          Será criado uma despesa para cada parcela, com a
                          descrição no formato "Descrição da despesa (1/5)".
                        </div>
                      </div>
                    )}

                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="flexSwitchCheckDefault"
                          checked={this.state.data?.isDone}
                          onChange={(e) =>
                            this.setState({
                              data: {
                                ...this.state.data,
                                isDone: e.target.checked,
                              },
                            })
                          }
                          value={this.state.data?.isDone ? 'true' : 'false'}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="flexSwitchCheckDefault"
                        >
                          Esta despesa foi paga?
                        </label>
                      </div>
                    </div>

                    <div className="mb-3">
                      <hr />
                    </div>

                    <div className="d-grid gap-2">
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={this.state.isSubmitting || !this.canSubmit()}
                        onClick={() => this.submit()}
                      >
                        {this.state.isSubmitting ? (
                          <LoadingButton />
                        ) : (
                          `${this.state.data?.id ? 'Atualizar' : 'Adicionar'} despesa`
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }
}
